/**
 * Responsive top navigation bar with scroll-aware styling.
 *
 * Features a glassmorphism AppBar that becomes more opaque on scroll,
 * a mobile hamburger drawer, an integrated language switcher, and
 * role-based link visibility (admin links for admins only).
 *
 * @module Navbar
 */

import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  useTheme,
  Avatar,
  alpha,
  Fade,
  Menu,
  MenuItem,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import TranslateIcon from '@mui/icons-material/Translate';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { GREEN, DARK_BLUE } from '../theme';

/** Available UI languages shown in the language switcher. */
const LANGUAGES = [
  { code: 'nl', label: 'Nederlands' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
];

/** Main navigation bar with desktop links, mobile drawer, and language selector. */
export default function Navbar() {
  const { user, logout, isAdmin } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [langAnchor, setLangAnchor] = useState<null | HTMLElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const publicLinks = [
    { label: t('nav.home'), path: '/' },
    { label: t('nav.showcase'), path: '/showcase' },
    { label: t('nav.blog'), path: '/blog' },
  ];

  const authLinks = user
    ? [
        { label: t('nav.dashboard'), path: '/dashboard' },
        ...(isAdmin ? [{ label: t('nav.admin'), path: '/admin' }] : []),
      ]
    : [];

  const allLinks = [...publicLinks, ...authLinks];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const drawerContent = (
    <Box sx={{ width: 280, pt: 3 }} onClick={() => setDrawerOpen(false)}>
      <Box sx={{ px: 3, pb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: 'primary.main' }}>
          {t('brand.booge')}
          <Box component="span" sx={{ color: 'secondary.main' }}>{t('brand.it')}</Box>
        </Typography>
      </Box>
      <List sx={{ px: 1 }}>
        {allLinks.map((link) => (
          <ListItem key={link.path} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              component={RouterLink}
              to={link.path}
              sx={{
                borderRadius: 2,
                mx: 1,
                '&:hover': {
                  bgcolor: alpha(GREEN, 0.08),
                },
              }}
            >
              <ListItemText primary={link.label} primaryTypographyProps={{ fontWeight: 500 }} />
            </ListItemButton>
          </ListItem>
        ))}
        {!user && (
          <>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton component={RouterLink} to="/login" sx={{ borderRadius: 2, mx: 1 }}>
                <ListItemText primary={t('nav.login')} primaryTypographyProps={{ fontWeight: 500 }} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton component={RouterLink} to="/register" sx={{ borderRadius: 2, mx: 1, bgcolor: alpha(GREEN, 0.1) }}>
                <ListItemText primary={t('nav.getStarted')} primaryTypographyProps={{ fontWeight: 600, color: GREEN }} />
              </ListItemButton>
            </ListItem>
          </>
        )}
        {user && (
          <ListItem disablePadding>
            <ListItemButton onClick={handleLogout} sx={{ borderRadius: 2, mx: 1 }}>
              <ListItemText primary={t('nav.logout')} primaryTypographyProps={{ fontWeight: 500 }} />
            </ListItemButton>
          </ListItem>
        )}
        {/* Language switcher in drawer */}
        <ListItem disablePadding sx={{ mt: 1 }}>
          <ListItemButton
            onClick={(e) => { e.stopPropagation(); setLangAnchor(e.currentTarget); }}
            sx={{ borderRadius: 2, mx: 1 }}
          >
            <TranslateIcon sx={{ mr: 1, fontSize: 20 }} />
            <ListItemText
              primary={LANGUAGES.find((l) => l.code === i18n.language)?.label || 'Nederlands'}
              primaryTypographyProps={{ fontWeight: 500 }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <AppBar
      position="sticky"
      color="inherit"
      component="nav"
      aria-label="Main navigation"
      sx={{
        bgcolor: scrolled ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottom: scrolled ? `1px solid ${alpha(DARK_BLUE, 0.08)}` : '1px solid transparent',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: scrolled ? `0 4px 30px ${alpha(DARK_BLUE, 0.08)}` : 'none',
      }}
    >
      <Toolbar sx={{ maxWidth: 1200, width: '100%', mx: 'auto', py: scrolled ? 0.5 : 1, transition: 'padding 0.3s ease' }}>
        <Typography
          variant="h5"
          component={RouterLink}
          to="/"
          sx={{
            fontWeight: 800,
            color: 'primary.main',
            textDecoration: 'none',
            flexGrow: 1,
            fontSize: scrolled ? '1.15rem' : '1.25rem',
            transition: 'font-size 0.3s ease',
            '&:hover': {
              '& .logo-accent': {
                color: '#2d9c1c',
              },
            },
          }}
        >
          {t('brand.booge')}
          <Box
            component="span"
            className="logo-accent"
            sx={{
              color: 'secondary.main',
              transition: 'color 0.3s ease',
            }}
          >
            {t('brand.it')}
          </Box>
        </Typography>

        {isMobile ? (
          <>
            <IconButton
              onClick={() => setDrawerOpen(true)}
              sx={{
                transition: 'transform 0.3s ease',
                '&:hover': { transform: 'scale(1.1)' },
              }}
            >
              <MenuIcon />
            </IconButton>
            <Drawer
              anchor="right"
              open={drawerOpen}
              onClose={() => setDrawerOpen(false)}
              PaperProps={{
                sx: {
                  borderRadius: '20px 0 0 20px',
                  boxShadow: '-8px 0 30px rgba(0,0,0,0.1)',
                },
              }}
            >
              {drawerContent}
            </Drawer>
          </>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {allLinks.map((link) => (
              <Button
                key={link.path}
                component={RouterLink}
                to={link.path}
                color="primary"
                sx={{
                  borderRadius: 2,
                  px: 2,
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    bottom: 6,
                    left: '50%',
                    transform: 'translateX(-50%) scaleX(0)',
                    width: '60%',
                    height: 2,
                    borderRadius: 1,
                    bgcolor: GREEN,
                    transition: 'transform 0.3s ease',
                  },
                  '&:hover::before': {
                    transform: 'translateX(-50%) scaleX(1)',
                  },
                }}
              >
                {link.label}
              </Button>
            ))}
            {!user ? (
              <Fade in timeout={500}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
                  <Button
                    component={RouterLink}
                    to="/login"
                    color="primary"
                    sx={{
                      borderRadius: 2,
                      px: 2,
                    }}
                  >
                    {t('nav.login')}
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/register"
                    variant="contained"
                    color="secondary"
                    sx={{ borderRadius: 25, px: 3 }}
                  >
                    {t('nav.getStarted')}
                  </Button>
                </Box>
              </Fade>
            ) : (
              <Fade in timeout={500}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 1 }}>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: GREEN,
                      fontSize: '0.85rem',
                      fontWeight: 700,
                    }}
                  >
                    {user.full_name?.charAt(0)?.toUpperCase()}
                  </Avatar>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                    {user.full_name}
                  </Typography>
                  <Button
                    onClick={handleLogout}
                    color="primary"
                    variant="outlined"
                    size="small"
                    sx={{ borderRadius: 25 }}
                  >
                    {t('nav.logout')}
                  </Button>
                </Box>
              </Fade>
            )}
          </Box>
        )}
        {/* Language switcher */}
        <IconButton
          onClick={(e) => setLangAnchor(e.currentTarget)}
          sx={{ ml: 1 }}
          size="small"
        >
          <TranslateIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={langAnchor}
          open={Boolean(langAnchor)}
          onClose={() => setLangAnchor(null)}
          PaperProps={{ sx: { borderRadius: 2, mt: 1 } }}
        >
          {LANGUAGES.map((lang) => (
            <MenuItem
              key={lang.code}
              selected={i18n.language === lang.code}
              onClick={() => {
                i18n.changeLanguage(lang.code);
                setLangAnchor(null);
              }}
              sx={{ fontWeight: i18n.language === lang.code ? 600 : 400 }}
            >
              {lang.label}
            </MenuItem>
          ))}
        </Menu>
      </Toolbar>
    </AppBar>
  );
}
